/* BOJOURNAL — portal-hero.js
   Scroll-position-driven reveal, written as CSS custom properties only;
   every visual decision stays in CSS.

   v2
   - Smoothed: the displayed progress eases toward the scroll position with a
     frame-rate independent damping, so wheel steps and trackpad jitter never
     reach the doors. A jump (End key, anchor) still settles in ~200ms.
   - Staged: meta text leaves first, then the doors part, the words lag a
     little behind their doors, the art settles, and the caption arrives last.
   - Reversible: it only reads scroll position.
   - Skipped for reduced-motion visitors (the static composition shows).
   - Survives Theme Editor load / unload / reorder; never double-binds. */
(() => {
  if (window.__bjPortal) return;
  window.__bjPortal = true;

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const clamp = (v, min = 0, max = 1) => Math.min(max, Math.max(min, v));
  const easeOut = (t) => 1 - Math.pow(1 - t, 3);
  const easeInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
  const DAMPING_MS = 70; // smaller = snappier; ~70ms reads as weight, not lag

  const portals = new Map();
  let raf = 0;
  let last = 0;

  const target = (state) => {
    const rect = state.el.getBoundingClientRect();
    const travel = state.el.offsetHeight - window.innerHeight;
    return travel > 0 ? clamp(-rect.top / travel) : 0;
  };

  const paint = (state, p) => {
    const { el, items, count } = state;
    const openEnd = count > 1 ? 0.45 : 0.66;

    const meta = 1 - easeOut(clamp(p / 0.14));
    const open = easeInOut(clamp((p - 0.02) / (openEnd - 0.02)));
    const settle = easeOut(clamp(p / (openEnd + 0.12)));
    const caption = easeOut(clamp((p - openEnd * 0.86) / 0.14));

    el.style.setProperty('--meta', meta.toFixed(4));
    el.style.setProperty('--open', open.toFixed(4));
    el.style.setProperty('--media-scale', (1.08 - 0.08 * settle).toFixed(4));
    el.style.setProperty('--caption', caption.toFixed(4));

    if (count > 1) {
      const span = (1 - openEnd - 0.08) / (count - 1);
      items.forEach((item, i) => {
        if (i === 0) return item.style.setProperty('--o', 1);
        const start = openEnd + 0.04 + (i - 1) * span;
        item.style.setProperty('--o', easeInOut(clamp((p - start) / (span * 0.6))).toFixed(4));
      });
    }

    // Header contrast while it floats over the revealed media.
    const tone = open > 0.55 && state.tone === 'dark' ? 'light' : 'dark';
    if (tone !== state.lastTone) {
      state.lastTone = tone;
      document.dispatchEvent(new CustomEvent('portal:tone', { detail: { tone } }));
    }
  };

  const frame = (now) => {
    const dt = last ? Math.min(64, now - last) : 16;
    last = now;
    let moving = false;
    portals.forEach((state) => {
      const goal = target(state);
      const k = 1 - Math.exp(-dt / DAMPING_MS);
      state.p += (goal - state.p) * k;
      if (Math.abs(goal - state.p) < 0.0004) state.p = goal;
      else moving = true;
      paint(state, state.p);
    });
    raf = moving ? requestAnimationFrame(frame) : 0;
    if (!moving) last = 0;
  };

  const kick = () => {
    if (!raf) raf = requestAnimationFrame(frame);
  };

  const setup = (el) => {
    if (reduce.matches || portals.has(el)) return;
    el.classList.add('is-live');
    const items = [...el.querySelectorAll('[data-portal-item]')];
    const state = { el, items, count: Math.max(items.length, 1), tone: el.dataset.mediaTone, lastTone: null, p: 0 };
    state.p = target(state); // no animation on load: start where the page is
    portals.set(el, state);
    paint(state, state.p);
  };

  const teardown = (root) => {
    portals.forEach((state, el) => {
      if (root.contains(el)) portals.delete(el);
    });
    if (!portals.size) document.dispatchEvent(new CustomEvent('portal:tone', { detail: { tone: 'dark' } }));
  };

  const init = (root = document) => root.querySelectorAll('[data-portal]').forEach(setup);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  window.addEventListener('scroll', kick, { passive: true });
  window.addEventListener('resize', kick, { passive: true });
  reduce.addEventListener('change', () => {
    if (!reduce.matches) return init();
    portals.forEach((state, el) => el.classList.remove('is-live'));
    portals.clear();
  });
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => teardown(event.target));
  document.addEventListener('shopify:section:reorder', kick);
})();
